import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { LogOut, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import '../styles/AdminDashboard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_API_KEY = process.env.REACT_APP_ADMIN_API_KEY;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [meetingRequests, setMeetingRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('7');
  const [showDeleteSingleDialog, setShowDeleteSingleDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  useEffect(() => {
    // Check authentication
    const isAuth = sessionStorage.getItem('adminAuth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }

    fetchMeetingRequests();
  }, [navigate]);

  const fetchMeetingRequests = async () => {
    try {
      const response = await axios.get(`${API}/meeting-requests`, {
        headers: {
          'X-API-Key': ADMIN_API_KEY
        }
      });
      setMeetingRequests(response.data);
      
      // Apply default 7-day filter
      const now = new Date();
      const filtered = response.data.filter(req => {
        const reqDate = new Date(req.created_at);
        const diffTime = now - reqDate;
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        return diffDays <= 7;
      });
      setFilteredRequests(filtered);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching meeting requests:', error);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    navigate('/admin');
  };

  const handleClearAllRecords = async () => {
    setIsDeleting(true);
    try {
      const response = await axios.delete(`${API}/meeting-requests`, {
        headers: {
          'X-API-Key': ADMIN_API_KEY
        }
      });
      
      // Refresh the data
      setMeetingRequests([]);
      setFilteredRequests([]);
      setShowDeleteDialog(false);
      
      alert(`Successfully deleted ${response.data.deleted_count} meeting requests`);
    } catch (error) {
      console.error('Error deleting meeting requests:', error);
      alert('Failed to delete meeting requests. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteSingleRecord = (record) => {
    setRecordToDelete(record);
    setShowDeleteSingleDialog(true);
  };

  const confirmDeleteSingleRecord = async () => {
    if (!recordToDelete) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/meeting-requests/${recordToDelete.id}`, {
        headers: {
          'X-API-Key': ADMIN_API_KEY
        }
      });
      
      // Remove from local state
      const updatedRequests = meetingRequests.filter(req => req.id !== recordToDelete.id);
      setMeetingRequests(updatedRequests);
      setFilteredRequests(updatedRequests.filter(req => {
        if (activeFilter === 'all') return true;
        const now = new Date();
        const reqDate = new Date(req.created_at);
        const diffDays = (now - reqDate) / (1000 * 60 * 60 * 24);
        return diffDays <= parseInt(activeFilter);
      }));
      
      setShowDeleteSingleDialog(false);
      setRecordToDelete(null);
    } catch (error) {
      console.error('Error deleting meeting request:', error);
      alert('Failed to delete meeting request. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const filterByDays = (days) => {
    const now = new Date();
    const filtered = meetingRequests.filter(req => {
      const reqDate = new Date(req.created_at);
      const diffTime = now - reqDate;
      const diffDays = diffTime / (1000 * 60 * 60 * 24);
      return diffDays <= days;
    });
    setFilteredRequests(filtered);
    setActiveFilter(days.toString());
  };

  const showAll = () => {
    setFilteredRequests(meetingRequests);
    setActiveFilter('all');
  };

  // Calculate stats
  const totalBookings = filteredRequests.length;
  const uniqueSalesReps = new Set(filteredRequests.map(req => req.sales_rep_name)).size;
  const uniqueCountries = new Set(filteredRequests.map(req => req.country)).size;
  const highIntentBookings = filteredRequests.filter(req => req.has_high_intent === true).length;

  // Calculate bookings by sales rep
  const bookingsBySalesRep = filteredRequests.reduce((acc, req) => {
    acc[req.sales_rep_name] = (acc[req.sales_rep_name] || 0) + 1;
    return acc;
  }, {});

  const formatDate = (dateString) => {
    // If the datestring doesn't have timezone info, append 'Z' to treat as UTC
    let dateToFormat = dateString;
    if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('T00:00:00')) {
      // Add 'Z' to indicate UTC if not already present
      dateToFormat = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    }
    
    const date = new Date(dateToFormat);
    
    // Format in Eastern Time
    const formatter = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
      timeZoneName: 'short'
    });
    
    return formatter.format(date);
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  return (
    <div className="admin-dashboard">
      <Helmet>
        <title>PourMyBev</title>
        <meta name="description" content="PourMyBev" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {/* Sidebar */}
      <div className="sidebar">
        <div className="sidebar-header">
          <h2>Admin Dashboard</h2>
        </div>
        <nav className="sidebar-nav">
          <a href="/admin/dashboard" className="nav-item active">Dashboard</a>
          <a href="/admin/sales-reps" className="nav-item">Sales Reps</a>
        </nav>
        <button onClick={handleLogout} className="logout-btn">
          <LogOut size={18} />
          Logout
        </button>
      </div>

      {/* Main Content */}
      <div className="dashboard-content">
        <div className="dashboard-header">
          <div>
            <h1>Bookings Dashboard</h1>
            <p className="subtitle">Track and monitor meeting requests and sales rep bookings</p>
          </div>
        </div>

        {/* Quick Filters */}
        <div className="quick-filters">
          <span className="filter-label">Quick Filter:</span>
          <Button 
            onClick={showAll}
            className={activeFilter === 'all' ? 'filter-btn active' : 'filter-btn'}
          >
            All Time
          </Button>
          <Button 
            onClick={() => filterByDays(7)}
            className={activeFilter === '7' ? 'filter-btn active' : 'filter-btn'}
          >
            Last 7 days
          </Button>
          <Button 
            onClick={() => filterByDays(30)}
            className={activeFilter === '30' ? 'filter-btn active' : 'filter-btn'}
          >
            Last 30 days
          </Button>
          <Button 
            onClick={() => filterByDays(90)}
            className={activeFilter === '90' ? 'filter-btn active' : 'filter-btn'}
          >
            Last 90 days
          </Button>
          <Button 
            onClick={() => filterByDays(365)}
            className={activeFilter === '365' ? 'filter-btn active' : 'filter-btn'}
          >
            Last Year
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Bookings</h3>
            <div className="stat-number">{totalBookings}</div>
            <p className="stat-description">Meeting requests in selected period</p>
          </div>
          <div className="stat-card">
            <h3>Unique Sales Reps</h3>
            <div className="stat-number">{uniqueSalesReps}</div>
            <p className="stat-description">Sales representatives with bookings</p>
          </div>
          <div className="stat-card">
            <h3>Countries</h3>
            <div className="stat-number">{uniqueCountries}</div>
            <p className="stat-description">International markets represented</p>
          </div>
          <div className="stat-card">
            <h3>Total High Intent Bookings</h3>
            <div className="stat-number">{highIntentBookings}</div>
            <p className="stat-description">Meeting requests with high intent</p>
          </div>
        </div>

        {/* Chart */}
        <div className="chart-section">
          <h3>Bookings by Sales Representative</h3>
          <p className="chart-subtitle">Total meeting requests per sales rep</p>
          <div className="bar-chart">
            {Object.entries(bookingsBySalesRep).map(([name, count]) => (
              <div key={name} className="bar-item">
                <div className="bar-label">{name}</div>
                <div className="bar-container">
                  <div 
                    className="bar-fill" 
                    style={{ width: `${(count / totalBookings) * 100}%` }}
                  >
                    <span className="bar-value">{count}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Meeting Request Log */}
        <div className="table-section">
          <h3>Meeting Request Log</h3>
          <p className="table-subtitle">Detailed list of all meeting requests for the selected date range</p>
          
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date & Timestamp</th>
                  <th>Sales Rep</th>
                  <th>State</th>
                  <th>Country</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="no-data">No meeting requests found</td>
                  </tr>
                ) : (
                  filteredRequests
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .map((request) => (
                      <tr key={request.id}>
                        <td>{formatDate(request.created_at)}</td>
                        <td>{request.sales_rep_name}</td>
                        <td>{request.state || '-'}</td>
                        <td>{request.country}</td>
                        <td>
                          <button
                            onClick={() => handleDeleteSingleRecord(request)}
                            className="delete-row-btn"
                            title="Delete this record"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Single Record Confirmation Dialog */}
      <AlertDialog open={showDeleteSingleDialog} onOpenChange={setShowDeleteSingleDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Meeting Request?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this meeting request?
              {recordToDelete && (
                <div className="mt-3 p-3 bg-gray-50 rounded">
                  <p><strong>Sales Rep:</strong> {recordToDelete.sales_rep_name}</p>
                  <p><strong>Country:</strong> {recordToDelete.country}</p>
                  {recordToDelete.state && <p><strong>State:</strong> {recordToDelete.state}</p>}
                  <p><strong>Date:</strong> {formatDate(recordToDelete.created_at)}</p>
                </div>
              )}
              <br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteSingleRecord}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Delete Record'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;